import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { DashboardProvider, useDashboardContext } from '../contexts/DashboardContext';
import DashboardSidebar from '../components/DashboardSidebar';
import ImageCropModal from '../components/shared/ImageCropModal';
import ReleaseModal from '../components/modals/ReleaseModal';
import { useModal } from '../contexts/ModalContext';
import 'aos/dist/aos.css';
import 'react-datepicker/dist/react-datepicker.css';

const DashboardShell = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Fermer le tiroir à chaque navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Verrouiller le scroll + Escape quand le tiroir est ouvert
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen]);

  const {
    data,
    loading,
    profilePic,
    handleLogout,
    handleProfilePicUpload,
    showReleaseModal,
    releaseMode,
    releaseSaving,
    releaseError,
    releaseForm,
    closeReleaseModal,
    handleReleaseFieldChange,
    handleCoverChange,
    handleTrackChange,
    handleSaveRelease,
    addTrackRow,
    removeTrackRow,
    moveTrack,
    coverRatioWarning,
    handleDropAudioFiles,
    quota,
    isUploadingProfilePic,
    showCropModal,
    imageToCrop,
    setShowCropModal,
    handleConfirmCrop,
  } = useDashboardContext();

  const { confirmLogout } = useModal();

  const onLogout = async () => {
    await confirmLogout({ onConfirm: handleLogout });
  };

  if (loading) {
    return (
      <div className="dashboard-shell">
        <div style={{ margin: 'auto', padding: 40, color: 'var(--ink-soft)' }}>Chargement du studio…</div>
      </div>
    );
  }

  return (
    <div className="dashboard-shell">
      {showCropModal && (
        <ImageCropModal
          image={imageToCrop}
          isUploading={isUploadingProfilePic}
          onConfirm={handleConfirmCrop}
          onCancel={() => setShowCropModal(false)}
        />
      )}

      <DashboardSidebar
        artist={data?.artist}
        profilePic={profilePic}
        onProfilePicUpload={handleProfilePicUpload}
        onLogout={onLogout}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />
      {mobileOpen && <div className="dashboard-overlay" onClick={() => setMobileOpen(false)} />}

      <div className="dashboard-main">
        <Outlet context={{ toggleMobileMenu: () => setMobileOpen(true), closeMobileMenu: () => setMobileOpen(false) }} />
      </div>

      <ReleaseModal
        show={showReleaseModal}
        onClose={closeReleaseModal}
        releaseMode={releaseMode}
        releaseForm={releaseForm}
        releaseError={releaseError}
        releaseSaving={releaseSaving}
        handleReleaseFieldChange={handleReleaseFieldChange}
        handleCoverChange={handleCoverChange}
        handleTrackChange={handleTrackChange}
        handleSaveRelease={handleSaveRelease}
        addTrackRow={addTrackRow}
        removeTrackRow={removeTrackRow}
        data={data}
        coverRatioWarning={coverRatioWarning}
        handleDropAudioFiles={handleDropAudioFiles}
        dragState={{ moveTrack }}
        quota={quota}
      />
    </div>
  );
};

const DashboardLayout = () => (
  <DashboardProvider>
    <DashboardShell />
  </DashboardProvider>
);

export default DashboardLayout;
